import { useEffect } from 'react'
import { THEME_ENUM } from '@/constants/theme.constant'
import { setMode, useAppSelector, useAppDispatch } from '@/store'
import type { Mode } from '@/types/theme'
import primeLightTheme from 'primereact/resources/themes/lara-light-indigo/theme.css?url'
import primeDarkTheme from 'primereact/resources/themes/lara-dark-indigo/theme.css?url'

const PRIME_THEME_LINK_ID = 'prime-react-theme'

function useDarkMode(): [
    isEnabled: boolean,
    onModeChange: (mode: Mode) => void
] {    
    const { MODE_DARK, MODE_LIGHT } = THEME_ENUM
    
    // Get mode from Redux store instead of separate localStorage
    const mode = useAppSelector((state) => state.theme.mode)
    const neo = useAppSelector((state) => state.theme.neo) ?? false
    const day = useAppSelector((state) => state.theme.day) ?? false
    const isEnabled = mode === MODE_DARK

    const dispatch = useAppDispatch()
    const onModeChange = (mode: Mode) => {
        dispatch(setMode(mode))
    }

    useEffect(() => {
        if (window === undefined) {
            return
        }
        const root = window.document.documentElement
        root.classList.remove(MODE_LIGHT, MODE_DARK)
        root.classList.add(mode)
        // Night and Day testing skins are separate opt-in classes. Never both.
        root.classList.toggle('neo', neo)
        root.classList.toggle('neo-day', day)

        const isDark = mode === MODE_DARK
        const themeColorMeta = document.querySelector(
            'meta[name="theme-color"]'
        )
        if (themeColorMeta) {
            themeColorMeta.setAttribute(
                'content',
                day ? '#dce8f8' : neo ? '#0a0a14' : isDark ? '#111827' : '#ffffff'
            )
        }

        // PrimeReact stylesheet: NEO uses lara-dark; classic (light AND dark)
        // keeps lara-light — classic dark is styled by the html.dark overrides
        // in src/index.css, exactly as before NEO existed.
        let primeThemeLink = document.getElementById(
            PRIME_THEME_LINK_ID
        ) as HTMLLinkElement | null
        if (!primeThemeLink) {
            primeThemeLink = document.createElement('link')
            primeThemeLink.id = PRIME_THEME_LINK_ID
            primeThemeLink.rel = 'stylesheet'
            document.head.appendChild(primeThemeLink)
        }
        const primeThemeHref = neo && !day ? primeDarkTheme : primeLightTheme
        if (primeThemeLink.getAttribute('href') !== primeThemeHref) {
            primeThemeLink.setAttribute('href', primeThemeHref)
        }
    }, [mode, neo, day, MODE_DARK, MODE_LIGHT])

    return [isEnabled, onModeChange]
}

export default useDarkMode
